import { createSlice } from '@reduxjs/toolkit';
import {
  fetchAddInstallationData,
  fetchEditInstallationData,
  fetchGetPullReasonsData,
  fetchGetComponentsData,
  fetchGetSubcomponentsData,
  fetchGetFailureCausesData,
  fetchGetFailureCauseDetailsData,
  fetchGetObservationsData,
  editInstallation,
  fetchGetWells,
  updateObservationsData,
} from '../../../services/installationHistory/InstallationHistoryService';

const initialState = {
  wellData: {
    data: undefined,
    loading: false,
    error: undefined as string | undefined,
  },
  addInstallationsData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  editInstallationData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  pullReasonsData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  componentsData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  subcomponentsData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  failureCausesData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  failureCauseDetailsData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  observationsData: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  updateObservationsData: {
    data: undefined,
    loading: false,
    error: undefined as string | undefined,
  },
  selectedInstallLiftType: '2',
  editInstallations: {
    data: undefined,
    loading: false,
    error: undefined as string | undefined,
  },
};

const CreateInstallSlice = createSlice({
  name: 'createInstall',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.wellData = { data: undefined, error: undefined, loading: false };
      state.addInstallationsData = { data: undefined, error: undefined, loading: false };
      state.editInstallationData = { data: undefined, error: undefined, loading: false };
      state.pullReasonsData = { data: undefined, error: undefined, loading: false };
      state.componentsData = { data: undefined, error: undefined, loading: false };
      state.subcomponentsData = { data: undefined, error: undefined, loading: false };
      state.failureCausesData = { data: undefined, error: undefined, loading: false };
      state.failureCauseDetailsData = { data: undefined, error: undefined, loading: false };
      state.observationsData = { data: undefined, error: undefined, loading: false };
      state.updateObservationsData = { data: undefined, error: undefined, loading: false };
      state.editInstallations = { data: undefined, error: undefined, loading: false };
    },
    setWellData: (state, action) => {
      state.wellData.data = action.payload;
    },
    setSelectedInstallLiftType: (state, action) => {
      state.selectedInstallLiftType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add Installation Data
      .addCase(fetchAddInstallationData.pending, (state) => {
        state.addInstallationsData.loading = true;
      })
      .addCase(fetchAddInstallationData.fulfilled, (state, action) => {
        state.addInstallationsData.loading = false;
        state.addInstallationsData.data = action.payload;
      })
      .addCase(fetchAddInstallationData.rejected, (state) => {
        state.addInstallationsData.loading = false;
      })
      // Edit Installation Data
      .addCase(fetchEditInstallationData.pending, (state) => {
        state.editInstallationData.loading = true;
      })
      .addCase(fetchEditInstallationData.fulfilled, (state, action) => {
        state.editInstallationData.loading = false;
        state.editInstallationData.data = action.payload;
      })
      .addCase(fetchEditInstallationData.rejected, (state) => {
        state.editInstallationData.loading = false;
      })
      // Pull Reasons Data
      .addCase(fetchGetPullReasonsData.pending, (state) => {
        state.pullReasonsData.loading = true;
      })
      .addCase(fetchGetPullReasonsData.fulfilled, (state, action) => {
        state.pullReasonsData.loading = false;
        state.pullReasonsData.data = action.payload;
      })
      .addCase(fetchGetPullReasonsData.rejected, (state) => {
        state.pullReasonsData.loading = false;
      })
      // Components Data
      .addCase(fetchGetComponentsData.pending, (state) => {
        state.componentsData.loading = true;
      })
      .addCase(fetchGetComponentsData.fulfilled, (state, action) => {
        state.componentsData.loading = false;
        state.componentsData.data = action.payload;
      })
      .addCase(fetchGetComponentsData.rejected, (state) => {
        state.componentsData.loading = false;
      })
      // Subcomponents Data
      .addCase(fetchGetSubcomponentsData.pending, (state) => {
        state.subcomponentsData.loading = true;
      })
      .addCase(fetchGetSubcomponentsData.fulfilled, (state, action) => {
        state.subcomponentsData.loading = false;
        state.subcomponentsData.data = action.payload;
      })
      .addCase(fetchGetSubcomponentsData.rejected, (state) => {
        state.subcomponentsData.loading = false;
      })
      // Failure Causes Data
      .addCase(fetchGetFailureCausesData.pending, (state) => {
        state.failureCausesData.loading = true;
      })
      .addCase(fetchGetFailureCausesData.fulfilled, (state, action) => {
        state.failureCausesData.loading = false;
        state.failureCausesData.data = action.payload;
      })
      .addCase(fetchGetFailureCausesData.rejected, (state) => {
        state.failureCausesData.loading = false;
      })
      // Failure Cause Details Data
      .addCase(fetchGetFailureCauseDetailsData.pending, (state) => {
        state.failureCauseDetailsData.loading = true;
      })
      .addCase(fetchGetFailureCauseDetailsData.fulfilled, (state, action) => {
        state.failureCauseDetailsData.loading = false;
        state.failureCauseDetailsData.data = action.payload;
      })
      .addCase(fetchGetFailureCauseDetailsData.rejected, (state) => {
        state.failureCauseDetailsData.loading = false;
      })
      // Observations Data
      .addCase(fetchGetObservationsData.pending, (state) => {
        state.observationsData.loading = true;
      })
      .addCase(fetchGetObservationsData.fulfilled, (state, action) => {
        state.observationsData.loading = false;
        state.observationsData.data = action.payload;
      })
      .addCase(fetchGetObservationsData.rejected, (state) => {
        state.observationsData.loading = false;
      })
      .addCase(editInstallation.pending, (state) => {
        state.editInstallations.loading = true;
        state.editInstallations.error = undefined;
      })
      .addCase(editInstallation.fulfilled, (state, action) => {
        state.editInstallations.loading = false;
        state.editInstallations.data = action.payload;
      })
      .addCase(editInstallation.rejected, (state, action) => {
        state.editInstallations.loading = false;
        state.editInstallations.error = action.error.message || 'Failed to edit installation';
      })
      // Well Data
      .addCase(fetchGetWells.pending, (state) => {
        state.wellData.loading = true;
        state.wellData.error = undefined;
      })
      .addCase(fetchGetWells.fulfilled, (state, action) => {
        state.wellData.loading = false;
        state.wellData.data = action.payload;
      })
      .addCase(fetchGetWells.rejected, (state, action) => {
        state.wellData.loading = false;
        state.wellData.error = action.error?.message || 'Failed to fetch wells';
      })
      // Update Observations Data
      .addCase(updateObservationsData.pending, (state) => {
        state.updateObservationsData.loading = true;
        state.updateObservationsData.error = undefined;
      })
      .addCase(updateObservationsData.fulfilled, (state, action) => {
        state.updateObservationsData.loading = false;
        state.updateObservationsData.data = action.payload;
      })
      .addCase(updateObservationsData.rejected, (state, action) => {
        state.updateObservationsData.loading = false;
        state.updateObservationsData.error = action.error?.message || 'Failed to update observations';
      });
  },
});

export const { setInitialState, setSelectedInstallLiftType } = CreateInstallSlice.actions;
export default CreateInstallSlice.reducer;
